import { ScrollValueProvider } from "@components/AnimatedStore";
import AppConfiguration from "@components/AppConfiguration";
import BranchConfiguration from "@components/BranchConfiguration";
import ErrorBoundaryComponent from "@components/ErrorBoundary";
import LBox from "@components/LBox";
import LGradient from "@components/LGradient";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { ThemeProvider } from "@shopify/restyle";
import { theme } from "@theme";
import { captureException } from "@utils/captureException";
import { initRudderStack } from "@utils/rudderstack";
import { LStripeProvider } from "@utils/stripe/LStripeProvider";
import { useStripeLinking } from "@utils/stripe/useStripeLinking";
import { Slot } from "expo-router";
import * as SystemUI from "expo-system-ui";
import { StatusBar } from "react-native";
import {
  BottomSheetHost,
  BottomSheetManagerProvider,
} from "react-native-bottom-sheet-stack";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { enableFreeze } from "react-native-screens";
import { SharedSwipeProvider } from "@components/SwipeList/SharedSwipeProvider";
import * as CustomerIO from "@utils/customerio";
import * as SplashScreen from "expo-splash-screen";
import { remoteEventForwarder } from "@utils/remoteEventForwarder";
import DeepLinkHandler from "@components/DeepLinkHandler";
import LinkTrackingHandler from "@components/LinkTrackingHandler";
import HandoffLandingHandler from "@components/HandoffLandingHandler";
import ReferralLinkHandler from "@components/ReferralLinkHandler";
import dayjs from "dayjs";
import en from "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import { useFonts } from "expo-font";
import { useEffect, type ComponentProps } from "react";
import branch from "branch-sdk";
import farcasterSdk from "@farcaster/miniapp-sdk";
import config from "@utils/config";
import WebFeedControls from "@components/WebFeedControls";
import PaymentsCallbackHandler from "@components/PaymentsCallbackHandler";
import "./styles.web.css";
import NavigationListener from "@utils/navigation/NavigationListener";
import { patchExpoRouterForFarcaster } from "@utils/patchExpoRouterForFarcaster";
import MiniAppLoginPrompt from "@components/MiniAppLoginPrompt";
import PlayerRuntimeProvider from "@utils/player/orchestration/PlayerProvider";
import { setupLogger } from "@utils/logger";
import { capabilities } from "../../../capabilities";
import { Toaster } from "sonner-native";

// Initialize logger before anything else.
setupLogger();

// Patch Expo Router for Farcaster Mini Apps
// Metro will use patchExpoRouterForFarcaster.web.ts on web, .ts (no-op stub) on native
patchExpoRouterForFarcaster();

dayjs.extend(utc);

dayjs.locale({
  ...en,
  weekStart: 1,
});

void remoteEventForwarder;

SystemUI.setBackgroundColorAsync(theme.colors.screenBg).catch((error) => {
  captureException(error, {
    channel: "ignore",
    area: "startup",
    operation: "set-system-ui-background-web",
  });
});
// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync().catch((error) => {
  captureException(error, {
    channel: "breadcrumb",
    area: "startup",
    operation: "prevent-splash-auto-hide-web",
  });
});

// Set the animation options. This is optional.
SplashScreen.setOptions({ fade: true });

enableFreeze();

initRudderStack().catch((error) => {
  captureException(error, {
    channel: "breadcrumb",
    area: "analytics",
    operation: "init-rudderstack-web",
  });
});
CustomerIO.initialize();

if (capabilities.allowed("branch")) {
  // No Journeys: their banners are inline scripts Branch injects at runtime,
  // which app-web's Content-Security-Policy (web/contentSecurityPolicy.ts)
  // blocks. Deep-link data, identity and events do not depend on them.
  branch.init(config.BRANCH.API_KEY, { no_journeys: true }, (error, data) => {
    if (error) {
      console.error("[INIT] branch.init callback - error:", error);
    } else {
      console.log("[INIT] branch.init callback - success:", data);
    }
  });
}

export function ErrorBoundary(
  props: Omit<ComponentProps<typeof ErrorBoundaryComponent>, "level">,
) {
  return <ErrorBoundaryComponent {...props} level="root" />;
}

const RootLayout = () => {
  // Load fonts for web using WOFF2 for better compression
  useFonts({
    LissenNanoIcons: require("../../../assets/nanoicons/LissenNanoIcons.ttf"),
    "Inter-Regular": require("../../../assets/fonts/Inter-Regular.woff2"),
    "Inter-Medium": require("../../../assets/fonts/Inter-Medium.woff2"),
    "Inter-SemiBold": require("../../../assets/fonts/Inter-SemiBold.woff2"),
    "Inter-Bold": require("../../../assets/fonts/Inter-Bold.woff2"),
    "Inter-ExtraBold": require("../../../assets/fonts/Inter-ExtraBold.woff2"),
  });

  // useReactNavigationDevTools(navRef);

  useStripeLinking();

  // Hide the HTML loading screen once React has mounted
  useEffect(() => {
    // Mark that React has mounted
    if (window.__appInitStages) {
      window.__appInitStages.reactMounted = Date.now();
      console.log(
        "React mounted after",
        window.__appInitStages.reactMounted - window.__appInitStages.htmlLoaded,
        "ms",
      );
    }

    // Call Farcaster SDK ready() only if we might be in a Farcaster context
    farcasterSdk
      .isInMiniApp()
      .then((isInMiniApp: boolean) => {
        if (isInMiniApp) {
          // Disable native gestures to prevent scroll conflicts with the webview modal
          return farcasterSdk.actions.ready({ disableNativeGestures: true });
        }
      })
      .catch((error: Error) => {
        console.log("Farcaster SDK initialization failed:", error.message);
      });

    // Function to attempt hiding the loading screen
    const attemptHide = () => {
      if (window.hideLoadingScreen) {
        const success = window.hideLoadingScreen();
        if (!success) {
          console.warn("Failed to hide loading screen - element not found");
        }
      } else if (window.__loadingSystemReady) {
        // Loading system is ready but function is missing (shouldn't happen)
        console.error("Loading system ready but hideLoadingScreen is missing");
      } else {
        // Loading system not ready yet, retry
        setTimeout(attemptHide, 50);
      }
    };

    // Small delay to ensure smooth transition after initial render
    const timer = setTimeout(attemptHide, 100);
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      <PlayerRuntimeProvider />
      <ThemeProvider theme={theme}>
        <GestureHandlerRootView style={{ flex: 1 }}>
          <LStripeProvider>
            <BottomSheetModalProvider>
              <BottomSheetManagerProvider id="app">
                <SharedSwipeProvider>
                  <ScrollValueProvider>
                    <StatusBar
                      barStyle="light-content"
                      backgroundColor="transparent"
                      translucent
                    />
                    <LGradient
                      flex={1}
                      colors={[
                        theme.colors.musicPlayerBottom,
                        theme.colors.musicPlayerTop,
                        theme.colors.musicPlayerBottom,
                      ]}
                      gradientDirection="horizontal"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <LBox
                        id="root-layout-container"
                        flexGrow={1}
                        width="100%"
                        height="100%"
                        backgroundColor="screenBg"
                        overflow="hidden"
                      >
                        <NavigationListener />
                        <BranchConfiguration />
                        <Slot />
                        <Toaster theme="light" />
                        <AppConfiguration />
                        <DeepLinkHandler />
                        <ReferralLinkHandler />
                        <LinkTrackingHandler />
                        <HandoffLandingHandler />
                        <PaymentsCallbackHandler />
                        <MiniAppLoginPrompt />
                        <BottomSheetHost />
                      </LBox>
                      <WebFeedControls />
                    </LGradient>
                  </ScrollValueProvider>
                </SharedSwipeProvider>
              </BottomSheetManagerProvider>
            </BottomSheetModalProvider>
          </LStripeProvider>
        </GestureHandlerRootView>
      </ThemeProvider>
    </>
  );
};

export default RootLayout;
